/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032: the dashboard's Inset shell (sidebar, desktop top bar, phone top bar).
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconBooks,
  IconBriefcase,
  IconCalendar,
  IconCalendarCheck,
  IconCopy,
  IconFlag,
  IconHome,
  IconInbox,
  IconKeyboard,
  IconLayoutDashboard,
  IconLock,
  IconLogout,
  IconMenu2,
  IconMoon,
  IconSearch,
  IconSelector,
  IconSettings,
  IconShield,
  IconCreditCard,
  IconSun,
  IconUsersGroup,
} from "@tabler/icons-react";
import { AuthContext, notifyActions, NOTIFICATION_TYPE } from "@lemonadetraining/web-shared";
import VariantAvatarImage from "@lemonadetraining/web-shared/components/VariantAvatarImage";
import VariantImg from "@lemonadetraining/web-shared/components/VariantImg";
import { PERMISSION } from "@lemonadetraining/contracts";
import {
  Avatar,
  AvatarFallback,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarTrigger,
  Skeleton,
  useSidebar,
  useTheme,
  CoachIcon,
  WorkoutIcon,
  MemberIcon,
} from "@lemonadetraining/web-ui";
import { useUserAccess } from "../hooks/useUserAccess";
import { useBoxFeatures } from "../hooks/useBoxFeatures";
import { usePendingRequestCount } from "../hooks/usePendingRequestCount";
import { loadBoxHeader } from "../hooks/useBoxOwnerUID";
import { formatBadgeCount } from "../lib/membershipRequestQueries";
import { coachesIndependently } from "../lib/today";
import { usePageTitle } from "../contexts/PageTitleContext";
import appIcon from "../assets/APP_ICON_2025.png";
import {
  activeShellKey,
  buildShellNav,
  findShellItem,
  resolveActiveKey,
  shellPageTitle,
  trainingCrumb,
  type ShellItem,
  type ShellItemKey,
  type ShellNav,
} from "./shellNav";

interface ShellUserState {
  user: {
    user: { uid: string; displayName: string; email: string; photoURL: string } | null;
    userInfoData: Record<string, unknown>;
  };
}

type BoxHeader =
  | { status: "none" }
  | { status: "loading" }
  | { status: "ready"; name: string; logoURL: string | null }
  | { status: "error" };

const ICONS: Record<ShellItemKey, React.ComponentType<{ className?: string }>> = {
  today: IconSun,
  setup: IconFlag,
  schedule: IconCalendar,
  members: IconUsersGroup,
  requests: IconInbox,
  memberships: IconCreditCard,
  programming: WorkoutIcon,
  team: CoachIcon,
  training: IconLayoutDashboard,
  library: IconBooks,
  book: IconCalendarCheck,
  membership: MemberIcon,
  admin: IconShield,
  coaching: IconBriefcase,
  settings: IconSettings,
};

const SUBTITLE_BY_ROLE = { owner: "Owner", admin: "Admin", coach: "Coach" } as const;

/** Everything the three shell pieces need, computed once per render. */
function useShellModel() {
  const { pathname, search } = useLocation();
  const segment = pathname.split("/")[1] || "dashboard";
  const rest = pathname.split("/").slice(2).join("/");
  const tab = new URLSearchParams(search).get("tab");

  const access = useUserAccess();
  const userInfoData = useSelector((s: ShellUserState) => s.user.userInfoData) || {};
  const boxID = access.boxID;
  // Same source as the old sidebar, so both read one cached settings document.
  const featuresBoxID = (userInfoData.boxID as string | undefined) ?? boxID ?? undefined;
  const { features, loading: featuresLoading } = useBoxFeatures(featuresBoxID);

  const nav: ShellNav = useMemo(
    () =>
      buildShellNav({
        hasBoxRights: access.hasBoxRights,
        hasCoachRights: access.hasCoachRights,
        hasAdminRights: access.hasAdminRights,
        isBoxAdmin: access.isBoxAdmin,
        isBoxCoach: access.isBoxCoach,
        isBoxStaff: access.isBoxStaff,
        coachesIndependently: coachesIndependently(access),
        canSetUpGym: access.canSetUpGym,
        canManageClasses: access.can(PERMISSION.MANAGE_CLASSES),
        bookingEnabled: features?.bookingEnabled === true,
        coachingEnabled: features?.coachingEnabled === true,
        hasBox: Boolean(boxID),
        isActiveMember: userInfoData.boxStatus === "active",
      }),
    // `can` is rebuilt each render; the booleans it reads are listed instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      access.hasBoxRights,
      access.hasCoachRights,
      access.hasAdminRights,
      access.isBoxAdmin,
      access.isBoxCoach,
      access.isBoxStaff,
      access.canSetUpGym,
      access.claims,
      features?.bookingEnabled,
      features?.coachingEnabled,
      boxID,
      userInfoData.boxStatus,
    ]
  );

  const pageTitle = usePageTitle();
  const activeKey = resolveActiveKey(nav, activeShellKey(rest, tab), rest);
  const active = findShellItem(nav, activeKey);
  const crumb = trainingCrumb(rest, tab, pageTitle);
  const title = crumb?.title ?? active?.item.label ?? shellPageTitle(rest);
  const parent = crumb?.parent ?? active?.group?.label ?? null;
  const loading = access.isLoading || (Boolean(boxID) && featuresLoading);

  return { access, nav, activeKey, active, title, parent, segment, loading, boxID };
}

function NavButton({
  item,
  active,
  segment,
  badge,
  srText,
}: {
  item: ShellItem;
  active: boolean;
  segment: string;
  badge?: React.ReactNode;
  /** Read with the name, and shown in the collapsed tooltip, where the badge is hidden. */
  srText?: string;
}) {
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const Icon = ICONS[item.key];
  if (item.locked) {
    return (
      <SidebarMenuItem data-testid={`shell-item-${item.key}`}>
        <SidebarMenuButton
          tooltip={`${item.label}, opens once your gym is created`}
          aria-disabled="true"
          className="cursor-not-allowed opacity-50 hover:bg-transparent"
          onClick={(event) => event.preventDefault()}
        >
          <Icon className="size-4" />
          <span>{item.label}</span>
          <IconLock className="ml-auto size-3.5" aria-hidden="true" />
          <span className="sr-only">, opens once your gym is created</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  return (
    <SidebarMenuItem data-testid={`shell-item-${item.key}`}>
      <SidebarMenuButton
        tooltip={srText ? `${item.label}, ${srText}` : item.label}
        isActive={active}
        aria-current={active ? "page" : undefined}
        onClick={() => {
          navigate(`/${segment}/${item.to}`);
          if (isMobile) setOpenMobile(false);
        }}
      >
        <Icon className="size-4" />
        <span>{item.label}</span>
        {srText && <span className="sr-only">, {srText}</span>}
      </SidebarMenuButton>
      {badge}
    </SidebarMenuItem>
  );
}

/** The count text lives in the button (NavButton srText); the badge is visual only. */
function requestsText(count: { count: number; failed: boolean }): string | undefined {
  if (count.failed) return "requests count unavailable, open to check";
  if (count.count > 0) {
    return `${formatBadgeCount(count.count)} ${count.count === 1 ? "request" : "requests"} waiting for you`;
  }
  return undefined;
}

function RequestsBadge({ count }: { count: { count: number; failed: boolean } }) {
  if (!count.failed && count.count <= 0) return null;
  return (
    <SidebarMenuBadge aria-hidden="true">
      <Badge
        variant={count.failed ? "outline" : "default"}
        className="h-5 min-w-5 px-1"
        data-testid="shell-requests-badge"
      >
        {count.failed ? "?" : formatBadgeCount(count.count)}
      </Badge>
    </SidebarMenuBadge>
  );
}

function useBoxHeader(boxID: string | null, wanted: boolean): BoxHeader {
  const [header, setHeader] = useState<BoxHeader>({ status: "none" });
  useEffect(() => {
    if (!wanted || !boxID) {
      setHeader({ status: "none" });
      return;
    }
    let live = true;
    setHeader({ status: "loading" });
    // The same cached read useUserAccess makes for the owner check.
    loadBoxHeader(boxID).then((box) => {
      if (!live) return;
      setHeader(
        box.name ? { status: "ready", name: box.name, logoURL: box.logoURL } : { status: "error" }
      );
    });
    return () => {
      live = false;
    };
  }, [boxID, wanted]);
  return header;
}

function ShellSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-2" data-testid="shell-skeleton" aria-busy="true">
      <span className="sr-only">Loading your menu</span>
      {Array.from({ length: 7 }).map((_, i) => (
        <SidebarMenuSkeleton key={i} showIcon />
      ))}
    </div>
  );
}

export function ShellSidebar() {
  const { access, nav, activeKey, segment, loading, boxID } = useShellModel();
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s: ShellUserState) => s.user.user);

  const requestCount = usePendingRequestCount(access.isBoxAdmin ? boxID : null);
  const showsGym = access.hasBoxRights || access.isBoxStaff;
  const boxHeader = useBoxHeader(boxID, showsGym);
  const headerLoading = loading || boxHeader.status === "loading";
  const gymName = boxHeader.status === "ready" ? boxHeader.name : null;
  const subtitle = access.displayRole ? SUBTITLE_BY_ROLE[access.displayRole] : null;
  const canCopyGymID = Boolean(boxID) && (access.isBoxAdmin || access.hasBoxRights);

  const initials = (user?.displayName || "U")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const signOut = async () => {
    const websiteUrl = process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
    sessionStorage.setItem("isLoggingOut", "true");
    try {
      await authCtx.userLogOut();
    } catch (error) {
      console.error("Logout error:", error);
    }
    sessionStorage.removeItem("isLoggingOut");
    const logoutUrl = new URL(websiteUrl);
    logoutUrl.searchParams.set("logout", "success");
    window.location.href = logoutUrl.toString();
  };

  const copyGymID = async () => {
    if (!boxID) return;
    try {
      await navigator.clipboard.writeText(boxID);
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.success,
          title: "Gym ID copied",
          message: `${boxID} is on your clipboard. Share it with support.`,
        })
      );
    } catch (error) {
      console.error("Failed to copy gym ID:", error);
      dispatch(
        notifyActions.sendNotification({
          severity: NOTIFICATION_TYPE.error,
          title: "Couldn't copy the gym ID",
          message: `Your gym ID is ${boxID}. Select it and copy it by hand.`,
        })
      );
    }
  };

  return (
    <Sidebar variant="inset" collapsible="icon" data-testid="shell-sidebar">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {headerLoading ? (
              <div className="flex items-center gap-2 p-2" data-testid="shell-header-skeleton">
                <Skeleton className="size-8 rounded-lg" />
                <div className="grid flex-1 gap-1.5 group-data-[collapsible=icon]:hidden">
                  <Skeleton className="h-3 w-28" />
                  <Skeleton className="h-2.5 w-14" />
                </div>
              </div>
            ) : (
              // Plain text, not a button: it has nothing to do until the gym switcher (F012).
              <div
                className="flex h-12 items-center gap-2 overflow-hidden rounded-md p-2 text-sm group-data-[collapsible=icon]:!size-8 group-data-[collapsible=icon]:!p-0"
                data-testid="shell-header"
              >
                <VariantImg
                  src={
                    showsGym && boxHeader.status === "ready" && boxHeader.logoURL
                      ? boxHeader.logoURL
                      : appIcon
                  }
                  size="200x200"
                  surface="boxLogo"
                  alt=""
                  className="size-8 shrink-0 rounded-lg object-cover"
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold" data-testid="shell-header-title">
                    {showsGym && gymName ? gymName : "Lemonade Training"}
                  </span>
                  {showsGym && subtitle && (
                    <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
                  )}
                </div>
              </div>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {loading ? (
          <ShellSkeleton />
        ) : (
          nav.groups.map((group) => (
            <SidebarGroup key={group.id} data-testid={`shell-group-${group.id}`}>
              {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
              <SidebarGroupContent>
                <SidebarMenu>
                  {group.items.map((item) => (
                    <NavButton
                      key={item.key}
                      item={item}
                      segment={segment}
                      active={activeKey === item.key}
                      badge={
                        item.key === "members" && access.isBoxAdmin ? (
                          <RequestsBadge count={requestCount} />
                        ) : undefined
                      }
                      srText={
                        item.key === "members" && access.isBoxAdmin
                          ? requestsText(requestCount)
                          : undefined
                      }
                    />
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        )}
      </SidebarContent>

      <SidebarFooter>
        {!loading && nav.footer.length > 0 && (
          <SidebarMenu>
            {nav.footer.map((item) => (
              <NavButton
                key={item.key}
                item={item}
                segment={segment}
                active={activeKey === item.key}
              />
            ))}
          </SidebarMenu>
        )}
        {user && (
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                    data-testid="shell-name-menu"
                  >
                    <Avatar className="size-8 rounded-lg">
                      <VariantAvatarImage src={user.photoURL} alt={user.displayName} />
                      <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user.displayName}</span>
                      <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                    </div>
                    <IconSelector className="ml-auto size-4" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="min-w-56 rounded-lg"
                  side="top"
                  align="start"
                  sideOffset={4}
                  collisionPadding={16}
                >
                  <DropdownMenuLabel className="font-normal">
                    <div className="grid text-sm leading-tight">
                      <span className="truncate font-semibold">{user.displayName}</span>
                      <span className="truncate text-xs text-muted-foreground">{user.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => navigate(`/${segment}/profile`)}
                    data-testid="shell-menu-profile"
                  >
                    <MemberIcon className="mr-2 size-4" />
                    My profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => window.dispatchEvent(new CustomEvent("lt:open-shortcuts-help"))}
                    data-testid="shell-menu-shortcuts"
                  >
                    <IconKeyboard className="mr-2 size-4" />
                    Keyboard shortcuts
                    <span className="ml-auto text-xs text-muted-foreground">?</span>
                  </DropdownMenuItem>
                  {canCopyGymID && (
                    <DropdownMenuItem onClick={copyGymID} data-testid="shell-menu-copy-gym-id">
                      <IconCopy className="mr-2 size-4" />
                      Copy gym ID
                      <span className="ml-auto max-w-24 truncate font-mono text-xs text-muted-foreground">
                        {boxID}
                      </span>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      window.location.href =
                        process.env.REACT_APP_WEBSITE_URL || "http://localhost:3000";
                    }}
                  >
                    <IconHome className="mr-2 size-4" />
                    Go to lemonade.training
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut} data-testid="shell-menu-logout">
                    <IconLogout className="mr-2 size-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

/** The one quick theme switch; Profile › Appearance keeps "match my device". */
function ThemeToggle({ className }: { className?: string }) {
  const { resolvedTheme, setTheme } = useTheme();
  const toDark = resolvedTheme !== "dark";
  const label = toDark ? "Switch to dark mode" : "Switch to light mode";
  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className={className}
      onClick={() => setTheme(toDark ? "dark" : "light")}
      aria-label={label}
      title={label}
      data-testid="shell-theme-toggle"
    >
      {toDark ? <IconMoon className="size-4" /> : <IconSun className="size-4" />}
    </Button>
  );
}

function openMovementSearch() {
  window.dispatchEvent(new CustomEvent("lt:open-movement-search"));
}

export function ShellDesktopHeader() {
  const { parent, title, loading } = useShellModel();
  return (
    <header
      className="hidden h-12 shrink-0 items-center gap-2 border-b px-4 text-foreground md:flex"
      data-testid="shell-desktop-header"
    >
      <SidebarTrigger className="-ml-1" />
      <Separator
        orientation="vertical"
        className="mr-1 data-[orientation=vertical]:h-4 data-[orientation=vertical]:self-center"
      />
      {loading ? (
        <Skeleton className="h-3 w-36" />
      ) : (
        title && (
          <Breadcrumb>
            <BreadcrumbList>
              {parent && parent !== title && (
                <>
                  <BreadcrumbItem className="hidden lg:block">{parent}</BreadcrumbItem>
                  <BreadcrumbSeparator className="hidden lg:block" />
                </>
              )}
              <BreadcrumbItem>
                <BreadcrumbPage data-testid="shell-breadcrumb-page">{title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        )
      )}
      <ThemeToggle className="ml-auto" />
      <Button
        variant="outline"
        size="sm"
        className="gap-2 text-muted-foreground"
        onClick={openMovementSearch}
        data-testid="shell-search"
      >
        <IconSearch className="size-4" />
        Search movements
        <kbd className="pointer-events-none rounded border px-1 font-sans text-[10px]">⌘K</kbd>
      </Button>
    </header>
  );
}

export function ShellMobileHeader() {
  const { toggleSidebar } = useSidebar();
  const { title, loading } = useShellModel();
  return (
    <header
      className="fixed left-0 right-0 top-0 z-50 flex items-center gap-2 border-b bg-background p-3 text-foreground shadow-sm md:hidden"
      data-testid="shell-mobile-header"
    >
      <Button variant="outline" size="icon-sm" onClick={toggleSidebar} aria-label="Open menu">
        <IconMenu2 className="size-4" />
      </Button>
      <span className="min-w-0 flex-1 truncate font-semibold">{loading ? "" : title}</span>
      <ThemeToggle />
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={openMovementSearch}
        aria-label="Search movements"
      >
        <IconSearch className="size-4" />
      </Button>
    </header>
  );
}
